import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { FitnessPlanService } from '../fitness-plan.service';
import { CreateTemplateComponent } from './create-template/create-template.component';

interface FitnessPlanTemplate {
  id: number;
  name: string;
  description: string;
  createdDate: string;
  createdBy: string;
  totalDays: number;
  totalExercises: number;
  days?: any[]; // Add days array for detailed template data
  isComplete?: boolean; // Add completion status
}

@Component({
  selector: 'app-fitness-templates',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatDialogModule,
    MatTableModule,
    MatSnackBarModule,
    FormsModule,
    CreateTemplateComponent,
  ],
  templateUrl: './fitness-templates.component.html',
  styleUrl: './fitness-templates.component.css',
})
export class FitnessTemplatesComponent implements OnInit {
  activeTab: 'new' | 'view' = 'new';
  templates: FitnessPlanTemplate[] = [];
  filteredTemplates: FitnessPlanTemplate[] = [];
  searchTerm: string = '';
  isLoading: boolean = false;
  showDeleteConfirmation: boolean = false;
  templateToDelete: FitnessPlanTemplate | null = null;

  constructor(
    private router: Router,
    private fitnessPlanService: FitnessPlanService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.loadTemplates();
  }

  setActiveTab(tab: 'new' | 'view') {
    this.activeTab = tab;
    if (tab === 'view') {
      this.loadTemplates();
    }
  }

  loadTemplates() {
    this.isLoading = true;
    this.fitnessPlanService.getAllFitnessPlanTemplates().subscribe(
      (response: any) => {
        // console.log('Templates response:', response);
        const rawTemplates = response.templates || response || [];

        // Process templates directly since they already include days data
        this.templates = rawTemplates.map((template: FitnessPlanTemplate) =>
          this.processTemplate(template)
        );

        this.filteredTemplates = [...this.templates];
        this.isLoading = false;
      },
      (error: any) => {
        console.error('Error loading templates:', error);
        this.templates = [];
        this.filteredTemplates = [];
        this.isLoading = false;
        this.snackBar.open('Error loading templates', 'Close', {
          duration: 3000,
          panelClass: ['error-snackbar'],
        });
      }
    );
  }

  // Process template to calculate exercise counts and completion status
  private processTemplate(template: FitnessPlanTemplate): FitnessPlanTemplate {
    const processedTemplate = { ...template };

    // console.log(`Processing template ${template.id}:`, {
    //   templateName: template.name,
    //   hasDays: !!template.days,
    //   daysLength: template.days?.length,
    //   daysData: template.days,
    // });

    if (
      template.days &&
      Array.isArray(template.days) &&
      template.days.length > 0
    ) {
      // Calculate total exercises across all days
      let totalExercises = 0;
      let completeDays = 0;

      template.days.forEach((day: any, dayIndex: number) => {
        // console.log(`Processing day ${dayIndex}:`, {
        //   dayName: day.dayName,
        //   isRestDay: day.isRestDay,
        //   hasExercises: !!day.exercises,
        //   exercisesLength: day.exercises?.length,
        //   exercises: day.exercises,
        // });

        if (!day.isRestDay) {
          if (day.exercises && Array.isArray(day.exercises)) {
            // Count exercises that have an exerciseId (the API uses exerciseId, not exercise)
            const validExercises = day.exercises.filter((exercise: any) => {
              const isValid =
                exercise.exerciseId && exercise.exerciseId.trim() !== '';
              // console.log(
              //   `Exercise with ID "${exercise.exerciseId}": valid = ${isValid}`
              // );
              return isValid;
            });
            totalExercises += validExercises.length;
            // console.log(
            //   `Day ${dayIndex} valid exercises: ${validExercises.length}, total so far: ${totalExercises}`
            // );

            // Day is complete if it has at least one valid exercise
            if (validExercises.length > 0) {
              completeDays++;
            }
          }
        } else {
          // Rest days are considered complete
          completeDays++;
          // console.log(
          //   `Day ${dayIndex} is rest day, complete days: ${completeDays}`
          // );
        }
      });

      processedTemplate.totalExercises = totalExercises;
      // Template is complete if all days have exercises or are rest days
      processedTemplate.isComplete = completeDays === template.days.length;

      // console.log(`Template ${template.id} final stats:`, {
      //   totalExercises,
      //   completeDays,
      //   totalDays: template.days.length,
      //   isComplete: processedTemplate.isComplete,
      // });
    } else {
      // console.log(
      //   `Template ${
      //     template.id
      //   } fallback - no days data, using existing totalExercises: ${
      //     template.totalExercises || 0
      //   }`
      // );
      // Fallback: Use existing totalExercises from API if available, otherwise set to 0
      processedTemplate.totalExercises = template.totalExercises || 0;

      // If we have exercise count > 0, assume it's somewhat complete, otherwise incomplete
      processedTemplate.isComplete = (template.totalExercises || 0) > 0;
    }

    return processedTemplate;
  }

  // Get display status for template
  getTemplateStatus(template: FitnessPlanTemplate): string {
    if (template.isComplete) {
      return 'Complete';
    } else {
      return 'Incomplete';
    }
  }

  // Get status class for styling
  getStatusClass(template: FitnessPlanTemplate): string {
    return template.isComplete ? 'status-complete' : 'status-incomplete';
  }

  searchTemplates() {
    if (!this.searchTerm.trim()) {
      this.filteredTemplates = [...this.templates];
      return;
    }

    this.isLoading = true;
    this.fitnessPlanService
      .searchFitnessPlanTemplates(this.searchTerm)
      .subscribe(
        (response: any) => {
          // console.log('Search response:', response);
          const searchResults = response.templates || response || [];

          // Process search results the same way as regular templates
          this.filteredTemplates = searchResults.map(
            (template: FitnessPlanTemplate) => this.processTemplate(template)
          );
          this.isLoading = false;
        },
        (error: any) => {
          console.error('Error searching templates:', error);
          // Fallback to local filtering
          this.filteredTemplates = this.templates.filter(
            (template) =>
              template.name
                .toLowerCase()
                .includes(this.searchTerm.toLowerCase()) ||
              (template.description || '')
                .toLowerCase()
                .includes(this.searchTerm.toLowerCase())
          );
          this.isLoading = false;
        }
      );
  }

  clearSearch() {
    this.searchTerm = '';
    this.filteredTemplates = [...this.templates];
  }

  createNewTemplate() {
    this.router.navigate(['/fitness-plan/templates/create']);
  }

  viewTemplate(template: FitnessPlanTemplate) {
    console.log('Viewing template:', template);
    // Navigate to view template details
    this.router.navigate(['/fitness-plan/templates/view', template.id]);
  }

  editTemplate(template: FitnessPlanTemplate) {
    console.log('Editing template:', template);
    // Navigate to edit template
    this.router.navigate(['/fitness-plan/templates/edit', template.id]);
  }

  confirmDeleteTemplate(template: FitnessPlanTemplate) {
    this.templateToDelete = template;
    this.showDeleteConfirmation = true;
  }

  deleteTemplate() {
    if (!this.templateToDelete) return;

    this.isLoading = true;
    this.fitnessPlanService
      .deleteFitnessPlanTemplate(this.templateToDelete.id)
      .subscribe(
        (response: any) => {
          console.log('Template deleted successfully:', response);
          this.snackBar.open('Template deleted successfully', 'Close', {
            duration: 3000,
            panelClass: ['success-snackbar'],
          });
          this.loadTemplates(); // Reload the templates list
          this.closeDeleteConfirmation();
        },
        (error: any) => {
          console.error('Error deleting template:', error);
          this.snackBar.open('Error deleting template', 'Close', {
            duration: 3000,
            panelClass: ['error-snackbar'],
          });
          this.isLoading = false;
          this.closeDeleteConfirmation();
        }
      );
  }

  closeDeleteConfirmation() {
    this.showDeleteConfirmation = false;
    this.templateToDelete = null;
  }

  duplicateTemplate(template: FitnessPlanTemplate) {
    console.log('Duplicating template:', template);
    // Load the template data and navigate to create page with prefilled data
    this.fitnessPlanService.getFitnessPlanTemplateById(template.id).subscribe(
      (response: any) => {
        console.log('Template data for duplication:', response);
        // Navigate to create template with query params containing the template data
        this.router.navigate(['/fitness-plan/templates/create'], {
          queryParams: { duplicate: template.id },
        });
      },
      (error: any) => {
        console.error('Error loading template for duplication:', error);
        this.snackBar.open('Error loading template for duplication', 'Close', {
          duration: 3000,
          panelClass: ['error-snackbar'],
        });
      }
    );
  }

  viewAllTemplates() {
    this.setActiveTab('view');
  }

  goBack() {
    this.router.navigate(['/fitness-plan']);
  }

  // Callback for when template is successfully created
  onTemplateCreated() {
    this.snackBar.open('Template created successfully!', 'Close', {
      duration: 3000,
      panelClass: ['success-snackbar'],
    });
    // Switch to view tab and reload templates
    this.setActiveTab('view');
    this.loadTemplates();
  }

  // Reset the create template form (for embedded mode)
  resetCreateForm() {
    // This could be implemented if needed to reset the embedded form
    // For now, switching to view tab serves the same purpose
  }
}
